import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';

const PREVIEW_MAX = 5;

/** Format byte count for display (same spirit as folder list total size). */
export function formatFootageBytes(n) {
  const num = Number(n) || 0;
  if (num === 0) return '0 MB';
  const mb = num / (1024 * 1024);
  if (mb >= 1024) {
    return `${(mb / 1024).toFixed(2)} GB`;
  }
  return `${mb.toFixed(2)} MB`;
}

/**
 * @param {'folders'|'videos'} mode
 * @param {object|null} deleteItem
 *   Folders: { id, name, videoCount, totalSizeBytes } | { items: same[] }
 *   Videos: { id, fileName, fileSize } | { items: same[] }
 */
export default function FootageDeleteConfirmDialog({
  mode,
  deleteItem,
  deleting,
  onClose,
  onConfirm,
}) {
  const isBulk = deleteItem?.items && Array.isArray(deleteItem.items);
  const bulkItems = isBulk ? deleteItem.items : [];
  const count = isBulk ? bulkItems.length : deleteItem ? 1 : 0;
  const open = Boolean(deleteItem) && count > 0;

  let totalVideos = 0;
  let totalBytes = 0;
  let hasUnknownVideoSize = false;

  if (mode === 'folders') {
    const rows = isBulk ? bulkItems : deleteItem ? [deleteItem] : [];
    for (const row of rows) {
      totalVideos += Number(row.videoCount) || 0;
      totalBytes += Number(row.totalSizeBytes) || 0;
    }
  } else {
    const rows = isBulk ? bulkItems : deleteItem ? [deleteItem] : [];
    totalVideos = rows.length;
    for (const row of rows) {
      if (row.fileSize != null && row.fileSize !== '') {
        totalBytes += Number(row.fileSize) || 0;
      } else {
        hasUnknownVideoSize = true;
      }
    }
  }

  let dialogTitle = 'Delete?';
  if (mode === 'folders') {
    if (isBulk && count > 1) {
      dialogTitle = `Delete ${count} folders?`;
    } else {
      dialogTitle = 'Delete folder?';
    }
  } else if (isBulk && count > 1) {
    dialogTitle = `Delete ${count} videos?`;
  } else {
    dialogTitle = 'Delete video?';
  }

  const preview = isBulk ? bulkItems.slice(0, PREVIEW_MAX) : [];
  const rest = isBulk ? Math.max(0, bulkItems.length - PREVIEW_MAX) : 0;

  const previewLabel = (row) =>
    mode === 'folders' ? row.name || '—' : row.fileName || '—';

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 1 }}>
          This will permanently delete{' '}
          <strong>{totalVideos}</strong> video{totalVideos === 1 ? '' : 's'} (~
          <strong>{formatFootageBytes(totalBytes)}</strong>
          {hasUnknownVideoSize ? '; some sizes unknown' : ''}).
        </Typography>
        {deleteItem && !isBulk && mode === 'folders' && (
          <Typography color='text.secondary' variant='body2' sx={{ mb: 0 }}>
            Folder: &quot;{deleteItem.name}&quot;. All objects for this folder in object storage will
            be removed.
          </Typography>
        )}
        {deleteItem && !isBulk && mode === 'videos' && (
          <Typography color='text.secondary' variant='body2' sx={{ mb: preview.length ? 1 : 0 }}>
            File: &quot;{deleteItem.fileName}&quot;.
          </Typography>
        )}
        {isBulk && (
          <Box sx={{ mt: 1 }}>
            {mode === 'folders' && (
              <Typography color='text.secondary' variant='body2' sx={{ mb: preview.length ? 1 : 0 }}>
                Selected folders and all of their objects in object storage will be removed.
              </Typography>
            )}
            {mode === 'videos' && (
              <Typography color='text.secondary' variant='body2' sx={{ mb: preview.length ? 1 : 0 }}>
                Selected files will be removed from object storage.
              </Typography>
            )}
            {preview.length > 0 && (
              <Box component='ul' sx={{ m: 0, pl: 2.5, my: 0 }}>
                {preview.map((it) => (
                  <li key={it.id}>
                    <Typography component='span' variant='body2'>
                      {previewLabel(it)}
                    </Typography>
                  </li>
                ))}
              </Box>
            )}
            {rest > 0 && (
              <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
                and {rest} more…
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={deleting}>
          Cancel
        </Button>
        <Button color='error' variant='contained' onClick={onConfirm} disabled={deleting}>
          {deleting ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
